import { ref, computed } from 'vue';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const defaultBounds = () => ({
  top: 0,
  bottom: window.innerHeight,
  left: 0,
  right: window.innerWidth,
});

export const IS_IOS = /iP(hone|od|ad)/.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const IOS_KEYBOARD_FRACTION = 0.45;

// iOS's on-screen keyboard shrinks window.visualViewport without shrinking
// window.innerHeight, so a panel positioned against the plain viewport can end
// up rendered behind the keyboard.
export const visibleViewport = () => {
  if (!IS_IOS) return defaultBounds();

  const vv = window.visualViewport;
  const bottom = vv.offsetTop + vv.height;
  const keyboardOpen = vv.width <= 600 && document.activeElement.matches('input, textarea');
  return {
    top: vv.offsetTop,
    bottom: keyboardOpen ? bottom - vv.height * IOS_KEYBOARD_FRACTION : bottom,
    left: vv.offsetLeft,
    right: vv.offsetLeft + vv.width,
  };
};

// Positions a teleported, `position: fixed` element against a trigger element:
// picks a preferred side, flips to the opposite side if it doesn't fit, then
// clamps to the viewport (or a custom `getBounds` area) so it never renders off-screen.
export function useFloatingPosition(triggerRef, contentRef, options = {}) {
  const position = ref({ top: 0, left: 0 });
  const flipped = ref(false);
  const availableSpace = ref(0);

  const update = () => {
    const trigger = triggerRef.value;
    const content = contentRef.value;
    if (!trigger || !content) return;

    const {
      placement = 'bottom',
      align = 'center',
      gap = 8,
      viewportPadding = 8,
      noFlip = false,
      preferMoreSpace = false,
      getBounds = defaultBounds,
      getFitHeight,
    } = options;

    const bounds = getBounds();
    const triggerRect = trigger.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    const fitHeight = getFitHeight ? getFitHeight(contentRect) : contentRect.height;

    const boundsWidth = bounds.right - bounds.left;
    const maxLeft = bounds.right - contentRect.width - viewportPadding;
    const maxTop = bounds.bottom - contentRect.height - viewportPadding;

    let left = triggerRect.left;
    if (align === 'end') {
      left = triggerRect.right - contentRect.width;
    } else if (align === 'center') {
      left = triggerRect.left + (triggerRect.width - contentRect.width) / 2;
    }
    // Content nearly as wide as the available area: center it instead of
    // anchoring to a trigger edge that may sit close to a screen edge.
    if (boundsWidth <= 600 && contentRect.width > boundsWidth * 0.85) {
      left = bounds.left + (boundsWidth - contentRect.width) / 2;
    }
    left = clamp(left, bounds.left + viewportPadding, Math.max(bounds.left + viewportPadding, maxLeft));

    const topSpace = triggerRect.top - bounds.top - gap - viewportPadding;
    const bottomSpace = bounds.bottom - triggerRect.bottom - gap - viewportPadding;

    let top;
    let isFlipped = placement === 'top';
    if (placement === 'top') {
      top = triggerRect.top - contentRect.height - gap;
      if (!noFlip && fitHeight > topSpace && (!preferMoreSpace || bottomSpace > topSpace)) {
        top = triggerRect.bottom + gap;
        isFlipped = false;
      }
    } else {
      top = triggerRect.bottom + gap;
      if (!noFlip && fitHeight > bottomSpace && (!preferMoreSpace || topSpace > bottomSpace)) {
        top = triggerRect.top - contentRect.height - gap;
        isFlipped = true;
      }
    }

    flipped.value = isFlipped;
    availableSpace.value = Math.max(0, isFlipped ? topSpace : bottomSpace);
    position.value = {
      left,
      top: clamp(top, bounds.top + viewportPadding, Math.max(bounds.top + viewportPadding, maxTop)),
    };
  };

  let tracking = false;
  const startTracking = () => {
    if (tracking) return;
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    tracking = true;
  };

  const stopTracking = () => {
    if (!tracking) return;
    window.removeEventListener('resize', update);
    window.removeEventListener('scroll', update, true);
    tracking = false;
  };

  const style = computed(() => ({
    position: 'fixed',
    left: `${position.value.left}px`,
    top: `${position.value.top}px`,
  }));

  return {
    style,
    flipped,
    availableSpace,
    update,
    startTracking,
    stopTracking,
  };
}
